/**
 * Dashboard Page
 * Main user dashboard with financial overview
 */

import { storage, requireAuth, requireProfile } from '../utils/storage.js';
import { profileApi } from '../services/api.js';
import { renderNavbar } from '../components/navbar.js';
import { renderFooter } from '../components/footer.js';
import {
  calculateMonthlySavings,
  estimateAffordableProperty,
  formatCurrency,
  formatPercentage,
  getSavingsHealth
} from '../utils/calculations.js';
import { initFeatherIcons } from '../utils/icons.js';

export function renderDashboard(container) {
  if (!requireAuth()) return;
  if (!requireProfile()) return;

  const user = storage.getUser();
  const profile = storage.getProfile();
  const expenses = storage.getExpenses();
  const guideProgress = storage.getGuideProgress();
  const homeGoal = storage.getHomeGoal();

  // Calculate financial metrics
  let monthlySavings = 0;
  let savingsPercentage = 0;
  let savingsHealth = { status: 'unknown', color: 'var(--gray-400)', label: 'Sin datos' };

  if (expenses) {
    const result = calculateMonthlySavings(profile.monthlyIncome, expenses);
    monthlySavings = result.monthlySavings;
    savingsPercentage = result.savingsPercentage;
    savingsHealth = getSavingsHealth(savingsPercentage);
  }

  // Estimate affordable property
  const affordability = estimateAffordableProperty(
    profile.monthlyIncome,
    profile.currentSavings
  );

  // Calculate savings for down payment
  const taxesAndExpensesPercent = 0.12; // 12% standard
  const downPaymentPercent = (100 - homeGoal.mortgagePercentage) / 100;
  const downPaymentOnly = homeGoal.price * downPaymentPercent;
  const expensesCost = homeGoal.price * taxesAndExpensesPercent;
  const downPaymentNeeded = downPaymentOnly + expensesCost;

  const savingsProgress = Math.min(100, (profile.currentSavings / downPaymentNeeded) * 100);

  container.innerHTML = `
    ${renderNavbar()}
    
    <main class="dashboard">
      <!-- Header -->
      <div class="dashboard-header">
        <div class="container">
          <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px;">
            <div>
              <h1 class="dashboard-welcome">Hola, ${user.email.split('@')[0]} 👋</h1>
              <p class="dashboard-date">${new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Content -->
      <div class="dashboard-content">
        <div class="container">
          <!-- Alert for incomplete expenses -->
          ${!expenses ? `
            <div class="alert alert-info" style="margin-bottom: 24px;">
              <i data-feather="info"></i>
              <div>
                <strong>Completa tu perfil financiero</strong>
                <p style="margin: 0;">Añade tus gastos mensuales para ver tu capacidad de ahorro real.</p>
              </div>
              <button id="addExpensesBtn" class="btn btn-primary btn-sm" style="margin-left: auto;">
                Añadir gastos
              </button>
            </div>
          ` : ''}
          
          <!-- Stats Grid -->
          <div class="dashboard-grid">
            <div class="card stat-card">
              <div class="stat-icon blue">
                <i data-feather="dollar-sign"></i>
              </div>
              <div class="stat-info">
                <h3>Capacidad de ahorro mensual</h3>
                <div class="stat-value">${expenses ? formatCurrency(monthlySavings) : '—'}</div>
                ${expenses ? `
                  <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                    <span class="stat-change" style="color: ${savingsHealth.color};">${savingsHealth.label}</span>
                    <button id="editExpensesMiniBtn" class="btn-link-subtle" style="font-size: 12px; border:none; background:none; cursor:pointer; padding: 0;">Editar</button>
                  </div>
                ` : '<a href="/gastos" data-link class="btn-link-subtle">Añade tus gastos →</a>'}
              </div>
            </div>
            
            <div class="card stat-card">
              <div class="stat-icon green">
                <i data-feather="home"></i>
              </div>
              <div class="stat-info">
                <h3>Ahorros para entrada</h3>
                <div class="stat-value" id="savingsDisplay">${formatCurrency(profile.currentSavings)}</div>
                <span style="color: var(--gray-500);">de ${formatCurrency(downPaymentNeeded)} necesarios</span>
                <div style="margin-top: 8px;">
                  <button id="editSavingsBtn" class="btn-edit-elegant">
                     <i data-feather="edit-2"></i> Editar ahorro
                  </button>
                </div>
              </div>
            </div>
            
            <div class="card stat-card">
              <div class="stat-icon purple">
                <i data-feather="pie-chart"></i>
              </div>
              <div class="stat-info">
                <h3>% de ingresos que ahorras</h3>
                <div class="stat-value">${expenses ? formatPercentage(savingsPercentage) : '—'}</div>
                <span style="color: var(--gray-500);">Recomendado: 20% de tus ingresos</span>
              </div>
            </div>
            
            <div class="card stat-card">
              <div class="stat-icon orange">
                <i data-feather="map-pin"></i>
              </div>
              <div class="stat-info">
                <h3>Ayudas en ${profile.region}</h3>
                <div class="stat-value">3</div>
                <a href="/ayudas" data-link style="color: var(--primary-600); font-size: 14px;">Ver ayudas disponibles →</a>
              </div>
            </div>
          </div>
          
          <!-- Progress Section -->
          <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 24px; margin-top: 32px;">
            <!-- Savings Progress -->
            <div class="card">
              <h3 style="margin-bottom: 24px; display: flex; align-items: center; gap: 8px;">
                <i data-feather="target" style="color: var(--primary-600);"></i>
                Progreso hacia tu vivienda
              </h3>
              
              <div class="progress-labeled">
                <div class="progress-header">
                  <span class="progress-label">
                    Ahorro para entrada (vivienda de ${formatCurrency(homeGoal.price)})
                    <button class="btn-text btn-edit-elegant" id="editGoalBtn">
                       <i data-feather="edit-2"></i> Editar
                    </button>
                  </span>
                  <span class="progress-value">${formatPercentage(savingsProgress, 0)}</span>
                </div>
                <div class="progress-bar">
                  <div class="progress-fill" style="width: ${savingsProgress}%;"></div>
                </div>
              </div>
              
              <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 24px;">
                <div style="text-align: center; padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                  <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Tienes</div>
                  <div style="font-weight: 700; color: var(--primary-600);">${formatCurrency(profile.currentSavings)}</div>
                </div>
                <div style="text-align: center; padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                  <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Te faltan</div>
                  <div style="font-weight: 700; color: var(--gray-700);">${formatCurrency(Math.max(0, downPaymentNeeded - profile.currentSavings))}</div>
                </div>
                <div style="text-align: center; padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                  <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Tiempo estimado</div>
                  <div style="font-weight: 700; color: var(--gray-700);">
                    ${expenses && monthlySavings > 0
      ? Math.ceil((downPaymentNeeded - profile.currentSavings) / monthlySavings) + ' meses'
      : '—'}
                  </div>
                </div>
              </div>
              
              <div style="margin-top: 24px; text-align: center;">
                <a href="/guia" data-link class="btn btn-primary btn-lg">
                  <i data-feather="book-open"></i>
                  Ver guía paso a paso
                </a>
              </div>
            </div>
            
            <!-- Quick Actions -->
            <div class="card">
              <h3 style="margin-bottom: 20px;">Acciones rápidas</h3>
              
              <div style="display: flex; flex-direction: column; gap: 12px;">
                <a href="/guia" data-link class="quick-action-link">
                  <div class="quick-action-icon" style="background: rgba(26, 82, 118, 0.1); color: var(--primary-600);">
                    <i data-feather="book-open"></i>
                  </div>
                  <div>
                    <div style="font-weight: 600;">Guía hipotecaria</div>
                    <div style="font-size: 12px; color: var(--gray-500);">Paso ${guideProgress.step} de 5</div>
                  </div>
                  <i data-feather="chevron-right" style="margin-left: auto; color: var(--gray-400);"></i>
                </a>
                
                <a href="/gastos" data-link class="quick-action-link">
                  <div class="quick-action-icon" style="background: rgba(16, 185, 129, 0.1); color: var(--success);">
                    <i data-feather="sliders"></i>
                  </div>
                  <div>
                    <div style="font-weight: 600;">Calculadora de gastos</div>
                    <div style="font-size: 12px; color: var(--gray-500);">${expenses ? 'Actualizar datos' : 'Completar'}</div>
                  </div>
                  <i data-feather="chevron-right" style="margin-left: auto; color: var(--gray-400);"></i>
                </a>
                
                <a href="/ahorro" data-link class="quick-action-link">
                  <div class="quick-action-icon" style="background: rgba(245, 158, 11, 0.1); color: var(--warning);">
                    <i data-feather="trending-up"></i>
                  </div>
                  <div>
                    <div style="font-weight: 600;">Plan de ahorro</div>
                    <div style="font-size: 12px; color: var(--gray-500);">Consejos personalizados</div>
                  </div>
                  <i data-feather="chevron-right" style="margin-left: auto; color: var(--gray-400);"></i>
                </a>
                
                <a href="/ayudas" data-link class="quick-action-link">
                  <div class="quick-action-icon" style="background: rgba(59, 130, 246, 0.1); color: var(--info);">
                    <i data-feather="gift"></i>
                  </div>
                  <div>
                    <div style="font-weight: 600;">Ayudas públicas</div>
                    <div style="font-size: 12px; color: var(--gray-500);">${profile.region}</div>
                  </div>
                  <i data-feather="chevron-right" style="margin-left: auto; color: var(--gray-400);"></i>
                </a>
                
                <a href="/plantillas" data-link class="quick-action-link">
                  <div class="quick-action-icon" style="background: rgba(26, 82, 118, 0.1); color: var(--primary-600);">
                    <i data-feather="file-text"></i>
                  </div>
                  <div>
                    <div style="font-weight: 600;">Plantillas Excel</div>
                    <div style="font-size: 12px; color: var(--gray-500);">Descarga herramientas</div>
                  </div>
                  <i data-feather="chevron-right" style="margin-left: auto; color: var(--gray-400);"></i>
                </a>
              </div>
            </div>
          </div>
          
          <!-- Your financial profile -->
          <div class="card" style="margin-top: 24px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
              <h3 style="display: flex; align-items: center; gap: 8px;">
                <i data-feather="user" style="color: var(--primary-600);"></i>
                Tu perfil financiero
              </h3>
              <a href="/onboarding" data-link class="btn btn-ghost btn-sm">
                <i data-feather="edit-2"></i>
                Editar
              </a>
            </div>
            
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 24px;">
              <div>
                <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Edad</div>
                <div style="font-weight: 600;">${profile.age} años</div>
              </div>
              <div>
                <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Situación laboral</div>
                <div style="font-weight: 600;">${getEmploymentLabel(profile.employmentStatus)}</div>
              </div>
              <div>
                <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Ingresos mensuales</div>
                <div style="font-weight: 600;">${formatCurrency(profile.monthlyIncome)}</div>
              </div>
              <div>
                <div style="font-size: 12px; color: var(--gray-500); margin-bottom: 4px;">Comunidad Autónoma</div>
                <div style="font-weight: 600;">${profile.region}</div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </main>
    
    ${renderFooter()}
    
    <style>
      .quick-action-link {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: var(--radius-lg);
        transition: var(--transition-fast);
      }
      
      .quick-action-link:hover {
        background: var(--gray-50);
      }
      
      .quick-action-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-lg);
      }
      
      .quick-action-icon svg {
        width: 18px;
        height: 18px;
      }
      
      @media (max-width: 968px) {
        .dashboard-content > .container > div[style*="grid-template-columns: 2fr 1fr"] {
          grid-template-columns: 1fr !important;
        }
      }
    </style>
    <!-- Goal Edit Modal -->
    <div class="modal-overlay" id="goalModal">
      <div class="modal">
        <div class="modal-header">
          <h3>Objetivo de Vivienda</h3>
          <button class="modal-close" id="closeGoalModal">
            <i data-feather="x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Precio de la vivienda</label>
            <div class="input-wrapper">
              <input type="number" id="goalPriceInput" class="form-input" value="${homeGoal.price}">
              <span class="input-suffix">€</span>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label">Porcentaje de hipoteca</label>
            <select id="goalMortgageInput" class="form-input">
              <option value="80" ${homeGoal.mortgagePercentage == 80 ? 'selected' : ''}>80% (Estándar)</option>
              <option value="90" ${homeGoal.mortgagePercentage == 90 ? 'selected' : ''}>90% (Jóvenes/Funcionarios)</option>
              <option value="100" ${homeGoal.mortgagePercentage == 100 ? 'selected' : ''}>100% (Casos especiales)</option>
            </select>
            <p class="form-hint">Lo habitual es que el banco te financie el 80% del valor.</p>
          </div>

          <div class="alert alert-info">
            <div style="font-size: 13px;">
              <strong>Ahorro necesario estimado:</strong><br>
              Entrada (${100 - homeGoal.mortgagePercentage}%): <span id="previewDownPayment"></span><br>
              Gastos (12%): <span id="previewExpenses"></span><br>
              <strong>Total: <span id="previewTotal"></span></strong>
            </div>
          </div>

          <button class="btn btn-primary w-full" id="saveGoalBtn">Guardar cambios</button>
        </div>
      </div>
    </div>

    <!-- Savings Edit Modal -->
    <div class="modal-overlay" id="savingsModal">
      <div class="modal">
        <div class="modal-header">
          <h3>Editar Ahorros</h3>
          <button class="modal-close" id="closeSavingsModal">
            <i data-feather="x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Ahorros actuales para la entrada</label>
            <div class="input-wrapper">
              <input type="number" id="savingsInput" class="form-input" value="${profile.currentSavings}" min="0" step="100">
              <span class="input-suffix">€</span>
            </div>
            <p class="form-hint">Introduce el total de ahorros que tienes destinados a la compra de vivienda.</p>
          </div>

          <div class="alert alert-info">
            <div style="font-size: 13px;">
              <strong>Necesitas ahorrar:</strong> ${formatCurrency(downPaymentNeeded)}<br>
              <span style="color: var(--gray-500);">Entrada (${100 - homeGoal.mortgagePercentage}%) + Gastos (12%) para una vivienda de ${formatCurrency(homeGoal.price)}</span>
            </div>
          </div>

          <button class="btn btn-primary w-full" id="saveSavingsBtn">Guardar cambios</button>
        </div>
      </div>
    </div>

    <style>
      .quick-action-link {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: var(--radius-lg);
        transition: var(--transition-fast);
      }
      
      .quick-action-link:hover {
        background: var(--gray-50);
      }
      
      .quick-action-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-lg);
      }
      
      .quick-action-icon svg {
        width: 18px;
        height: 18px;
      }
      
          grid-template-columns: 1fr !important;
        }
      }

      .btn-edit-elegant {
        background: white;
        border: 1px solid var(--gray-200);
        padding: 4px 10px;
        border-radius: 20px;
        font-size: 11px;
        font-weight: 500;
        color: var(--gray-600);
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s;
        cursor: pointer;
        margin-left: 8px;
        box-shadow: 0 1px 2px rgba(0,0,0,0.05);
      }
      
      .btn-edit-elegant:hover {
        border-color: var(--primary-300);
        color: var(--primary-600);
        transform: translateY(-1px);
        box-shadow: 0 4px 6px rgba(0,0,0,0.05);
      }

      .btn-edit-elegant svg {
        width: 12px;
        height: 12px;
      }

      .btn-link-subtle {
        color: var(--primary-600);
        font-size: 14px;
        font-weight: 500;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
      
      .btn-link-subtle:hover {
        text-decoration: underline;
      }
    </style>
  `;

  // --- Event Listeners ---

  // Navigate to Expenses
  const addExpensesBtn = document.getElementById('addExpensesBtn');
  if (addExpensesBtn) {
    addExpensesBtn.addEventListener('click', () => {
      window.router.navigate('/gastos');
    });
  }

  const editExpensesMiniBtn = document.getElementById('editExpensesMiniBtn');
  if (editExpensesMiniBtn) {
    editExpensesMiniBtn.addEventListener('click', () => {
      window.router.navigate('/gastos');
    });
  }

  // Modal Logic
  const modal = document.getElementById('goalModal');
  const editBtn = document.getElementById('editGoalBtn');
  const closeBtn = document.getElementById('closeGoalModal');
  const saveBtn = document.getElementById('saveGoalBtn');
  const priceInput = document.getElementById('goalPriceInput');
  const mortgageInput = document.getElementById('goalMortgageInput');

  // Open Modal
  if (editBtn) {
    editBtn.addEventListener('click', () => {
      modal.classList.add('active');
      updatePreview();
    });
  }

  // Close Modal
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      modal.classList.remove('active');
    });
  }

  // Close on outside click
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.remove('active');
    });
  }

  // Live Preview Calculation
  function updatePreview() {
    const price = Number(priceInput.value) || 0;
    const mortgagePct = Number(mortgageInput.value) || 80;

    const downPaymentPct = (100 - mortgagePct) / 100;
    const downVal = price * downPaymentPct;
    const expVal = price * 0.12;
    const total = downVal + expVal;

    document.getElementById('previewDownPayment').textContent = formatCurrency(downVal);
    document.getElementById('previewExpenses').textContent = formatCurrency(expVal);
    document.getElementById('previewTotal').textContent = formatCurrency(total);
  }

  if (priceInput) priceInput.addEventListener('input', updatePreview);
  if (mortgageInput) mortgageInput.addEventListener('change', updatePreview);

  // Save Changes (Home Goal)
  if (saveBtn) {
    saveBtn.addEventListener('click', async () => {
      const newGoal = {
        price: Number(priceInput.value) || 200000,
        mortgagePercentage: Number(mortgageInput.value) || 80
      };

      storage.setHomeGoal(newGoal);
      modal.classList.remove('active');

      // Persist to backend
      try {
        await profileApi.updateProfile({
          homeGoalPrice: newGoal.price,
          homeGoalMortgagePct: newGoal.mortgagePercentage
        });
      } catch (err) {
        console.warn('Could not save home goal to backend:', err);
      }

      // Re-render dashboard to show changes
      renderDashboard(container);
    });
  }

  // --- Savings Modal Logic ---
  const savingsModal = document.getElementById('savingsModal');
  const editSavingsBtn = document.getElementById('editSavingsBtn');
  const closeSavingsModal = document.getElementById('closeSavingsModal');
  const saveSavingsBtn = document.getElementById('saveSavingsBtn');
  const savingsInput = document.getElementById('savingsInput');

  if (editSavingsBtn) {
    editSavingsBtn.addEventListener('click', () => {
      savingsModal.classList.add('active');
    });
  }

  if (closeSavingsModal) {
    closeSavingsModal.addEventListener('click', () => {
      savingsModal.classList.remove('active');
    });
  }

  if (savingsModal) {
    savingsModal.addEventListener('click', (e) => {
      if (e.target === savingsModal) savingsModal.classList.remove('active');
    });
  }

  if (saveSavingsBtn) {
    saveSavingsBtn.addEventListener('click', async () => {
      const newSavings = Number(savingsInput.value) || 0;

      // Update profile in localStorage
      const currentProfile = storage.getProfile();
      currentProfile.currentSavings = newSavings;
      storage.setProfile(currentProfile);

      savingsModal.classList.remove('active');

      // Persist to backend
      try {
        await profileApi.updateProfile({ currentSavings: newSavings });
      } catch (err) {
        console.warn('Could not save savings to backend:', err);
      }

      // Re-render dashboard
      renderDashboard(container);
    });
  }

  // Initialize icons
  initFeatherIcons();
}

function getEmploymentLabel(status) {
  const labels = {
    'employed_indefinite': 'Contrato indefinido',
    'employed_temporary': 'Contrato temporal',
    'self_employed': 'Autónomo/a',
    'civil_servant': 'Funcionario/a',
    'unemployed': 'Desempleado/a',
    'student': 'Estudiante',
    'retired': 'Jubilado/a',
    'other': 'Otros'
  };
  return labels[status] || status;
}
